import { z } from 'zod';

import { VoiceCallTransferCallToolDestinationSchema } from './transferToolConfig.js';
import {
  CartesiaVoicePersona,
  ElevenLabsVoicePersona,
  OPENAI_REALTIME_COMPATIBLE_VOICES,
  OpenAiRealtimeVoicePersona,
  OpenAiStandardVoicePersona,
  VoiceCallBackgroundSound,
  VoiceCallFirstMessageMode,
  VoiceCallIdleMessage,
  VoiceCallLanguage,
  VoiceCallModel,
  VoiceCallOptionMode,
  VoiceProvider,
  isElevenLabsCompatibleVoicePersona,
  isRealTimeExclusiveVoicePersona,
  isRealTimeModel,
  isRealtimeCompatibleVoicePersona,
} from './voiceCallBlock.js';
import { isDefined } from '../../../utils.js';
import {
  ASSISTANT_VOICE_SPEED_RANGE_BY_VOICE_PROVIDER,
  END_CALL_MESSAGE_MAX_LENGTH,
  VOICEMAIL_MESSAGE_MAX_LENGTH,
} from '../../assistant/assistantDefinition/constants.js';
import { AssistantToolType } from '../../assistant/toolDefinition/index.js';
import { VoicePersonaProvider } from '../../assistant/voicePersona.js';
import {
  BaseFlowBlockSchema,
  BlockType,
  DurationSchemaWithMax,
  DurationUnits,
  ProducedType,
  returnTypeSchema,
} from '../base/baseSchema.js';
import { BlockValueUsPhoneNumberSchema } from '../base/blockValueUsPhoneNumberSchema.js';
import { OUTBOUND_CALL_AND_SMS_ALLOWED_COUNTRIES } from '../base/phoneNumberCountries.js';

const VOICE_SPEED_RANGE_BY_VOICE_PROVIDER: Partial<
  Record<VoiceProvider, { min: number; max: number; displayName: string }>
> = {
  [VoiceProvider.Cartesia]:
    ASSISTANT_VOICE_SPEED_RANGE_BY_VOICE_PROVIDER[VoicePersonaProvider.Cartesia],
  [VoiceProvider.ElevenLabs]:
    ASSISTANT_VOICE_SPEED_RANGE_BY_VOICE_PROVIDER[VoicePersonaProvider.ElevenLabs],
};

export const VoiceCallBlockConfigSchema = z
  .object({
    calleePhoneNumber: BlockValueUsPhoneNumberSchema({
      isRequired: true,
      allowedCountries: OUTBOUND_CALL_AND_SMS_ALLOWED_COUNTRIES,
    }),
    callerPhoneNumberId: z.string().uuid(),
    firstMessage: z.string(),
    firstMessageMode: z
      .nativeEnum(VoiceCallFirstMessageMode)
      .optional()
      .default(VoiceCallFirstMessageMode.AssistantWaitsForUser),
    prompt: z.string(),
    modelName: z.nativeEnum(VoiceCallModel).optional().default(VoiceCallModel.gpt_4_1),
    voicemailMessage: z.string().max(VOICEMAIL_MESSAGE_MAX_LENGTH).optional(),
    language: z.nativeEnum(VoiceCallLanguage).optional().default(VoiceCallLanguage.English),
    voiceProvider: z.nativeEnum(VoiceProvider).optional().default(VoiceProvider.ElevenLabs),
    voicePersona: z
      .union([
        z.nativeEnum(ElevenLabsVoicePersona),
        z.nativeEnum(OpenAiStandardVoicePersona),
        z.nativeEnum(OpenAiRealtimeVoicePersona),
        z.nativeEnum(CartesiaVoicePersona),
      ])
      .optional()
      .default(ElevenLabsVoicePersona.Sarah),
    timeout: DurationSchemaWithMax('timeout', {
      value: 4,
      units: DurationUnits.Hours,
    }),
    maxCallDuration: DurationSchemaWithMax('maxCallDuration', {
      value: 1,
      units: DurationUnits.Hours,
    }).optional(),
    tools: z.array(z.nativeEnum(AssistantToolType)).optional(),
    metadata: z.string().optional(),
    decisionTreeId: z.string().nullish(),
    endCallMessage: z.string().max(END_CALL_MESSAGE_MAX_LENGTH).optional(),
    emotionRecognition: z.nativeEnum(VoiceCallOptionMode).optional(),
    endCallFunction: z.nativeEnum(VoiceCallOptionMode).optional(),
    startSpeakingSmartEndpointing: z.nativeEnum(VoiceCallOptionMode).optional(),
    backgroundDenoising: z.nativeEnum(VoiceCallOptionMode).optional(),
    backgroundSound: z
      .nativeEnum(VoiceCallBackgroundSound)
      .optional()
      .default(VoiceCallBackgroundSound.Off),
    startSpeakingWaitSeconds: z.number().min(0).max(5).optional().default(2.0),
    voiceSimilarityBoost: z.number().min(0).max(1).optional().default(0.6),
    voiceSpeed: z.number().optional().default(1.0),
    voiceStability: z.number().min(0).max(1).optional().default(0.3),
    idleMessages: z.array(z.nativeEnum(VoiceCallIdleMessage)).optional(),
    idleMessageMaxSpokenCount: z.number().min(1).max(10).optional().default(3),
    idleTimeoutSeconds: z.number().min(5).max(60).optional().default(5),
    stopSpeakingNumberOfWords: z.number().min(0).max(10).optional().default(5),
    stopSpeakingVoiceSeconds: z.number().min(0).max(0.5).optional().default(0.5),
    stopSpeakingVoiceBackOffSeconds: z.number().min(0).max(10).optional().default(0.5),
    modelTemperature: z.number().min(0).max(2).optional().default(0.3),
    transferCallTool: z
      .object({
        destinations: z.array(VoiceCallTransferCallToolDestinationSchema),
      })
      .optional(),
    toolToToolDescriptionMap: z.record(z.nativeEnum(AssistantToolType), z.string()).optional(),
    enableSsmlParsing: z.boolean().optional().default(false),
    validHours: z
      .object({
        start: z.number().min(0).max(23),
        end: z.number().min(0).max(23),
      })
      .optional(),
    preAssistantMessage: z.string().optional(),
  })
  .refine(
    (config) => {
      // If using a realtime model, must use OpenAI voice provider
      if (isDefined(config.modelName) && isRealTimeModel(config.modelName)) {
        return config.voiceProvider === VoiceProvider.OpenAi;
      }
      return true;
    },
    {
      message:
        'OpenAI Realtime LLM Models require OpenAI as the voice provider. Please select OpenAI voice provider.',
      path: ['voiceProvider'],
    }
  )
  .refine(
    (config) => {
      // If using 11Labs voice provider, must use 11Labs voice persona
      if (config.voiceProvider === VoiceProvider.ElevenLabs && isDefined(config.voicePersona)) {
        return isElevenLabsCompatibleVoicePersona(config.voicePersona);
      }
      return true;
    },
    {
      message:
        'Voice Provider is set to 11Labs, but the selected Voice is not an 11Labs voice. Please select an 11Labs voice or change the provider.',
      path: ['voicePersona'],
    }
  )
  .refine(
    (config) => {
      // If using a realtime model, the voice must be realtime-compatible
      if (
        isDefined(config.modelName) &&
        isRealTimeModel(config.modelName) &&
        isDefined(config.voicePersona)
      ) {
        return isRealtimeCompatibleVoicePersona(config.voicePersona);
      }
      return true;
    },
    {
      message: `LLM Model is a realtime model, but the selected Voice is not realtime-compatible. Valid voices: ${OPENAI_REALTIME_COMPATIBLE_VOICES.join(', ')}.`,
      path: ['voicePersona'],
    }
  )
  .refine(
    (config) => {
      // If using realtime-exclusive voices, must use a realtime model
      if (isDefined(config.voicePersona) && isRealTimeExclusiveVoicePersona(config.voicePersona)) {
        return isDefined(config.modelName) && isRealTimeModel(config.modelName);
      }
      return true;
    },
    {
      message: `Voice is a realtime-exclusive voice (${Object.values(OpenAiRealtimeVoicePersona).join('/')}), but the LLM Model is not a realtime model. Please select a realtime model or choose a different voice.`,
      path: ['voicePersona'],
    }
  )
  .superRefine((config, ctx) => {
    if (isDefined(config.voiceProvider) && isDefined(config.voiceSpeed)) {
      const voiceSpeedRange =
        config.voiceProvider in VOICE_SPEED_RANGE_BY_VOICE_PROVIDER
          ? VOICE_SPEED_RANGE_BY_VOICE_PROVIDER[config.voiceProvider]
          : undefined;
      if (isDefined(voiceSpeedRange)) {
        if (config.voiceSpeed < voiceSpeedRange.min || config.voiceSpeed > voiceSpeedRange.max) {
          ctx.addIssue({
            path: ['voiceSpeed'],
            code: z.ZodIssueCode.custom,
            message: `Voice Provider is set to ${voiceSpeedRange.displayName}, but the voice speed is not between ${voiceSpeedRange.min} and ${voiceSpeedRange.max}. Please set the voice speed between ${voiceSpeedRange.min} and ${voiceSpeedRange.max}.`,
          });
        }
      }
    }
  });

export type VoiceCallBlockConfig = z.infer<typeof VoiceCallBlockConfigSchema>;

export const VoiceCallBlockSchema = BaseFlowBlockSchema.extend({
  type: z.literal(BlockType.VoiceCall),
  config: VoiceCallBlockConfigSchema,
  returnType: returnTypeSchema(ProducedType.VoiceCall),
});
export type VoiceCallBlock = z.infer<typeof VoiceCallBlockSchema>;

export const VoiceCallReturnDataSchema = z
  .object({
    // data returned from executeVoiceCallBlock
    callId: z.string(),
    phoneNumberId: z.string(),
    type: z.string(),
    // data added from createVoiceCall
    notableVoiceCall: z
      .object({
        id: z.string(),
        created: z.string(),
        updated: z.string(),
        sessionId: z.string(),
        providerCallId: z.string(),
        type: z.string(),
        assistant: z.record(z.string(), z.unknown()),
        toPhoneNumber: z.string(),
        fromPhoneNumber: z.string(),
        callStartTime: z.string(), // date-time string
        provider: z.string(),
        callEndTime: z.string().nullable().optional(), // date-time string
        endedReason: z.string().nullable().optional(),
        callCost: z
          .record(z.string(), z.object({}).passthrough().or(z.undefined()))
          .nullable()
          .optional(),
        summary: z.string().nullable().optional(),
        successEvaluation: z.string().nullable().optional(),
        gsPath: z.string().nullable().optional(),
      })
      .passthrough()
      .optional(),
    // data added in handleVoiceCallBlockCallback
    analysis: z
      .object({
        summary: z.string().optional(),
        successEvaluation: z.string().optional(),
      })
      .optional(),
    summary: z.string().optional(),
    transcript: z.string().optional(),
    messages: z
      .array(
        z
          .object({
            role: z.string(),
            time: z.number(),
            message: z.string().optional(),
            secondsFromStart: z.number(),
            endTime: z.number().optional(),
            duration: z.number().optional(),
            source: z.string().optional(),
          })
          .passthrough()
      )
      .optional(),
    durationSeconds: z.number().optional(),
    endedReason: z.string().optional(),
  })
  .passthrough();
export type VoiceCallReturnData = z.infer<typeof VoiceCallReturnDataSchema>;

export const VoiceCallV2ReturnDataSchema = z
  .object({
    notableVoiceCall: z
      .object({
        id: z.string(),
        created: z.string(),
        updated: z.string(),
        sessionId: z.string(),
        providerCallId: z.string(),
        type: z.string(),
        assistant: z.record(z.string(), z.unknown()),
        toPhoneNumber: z.string(),
        fromPhoneNumber: z.string(),
        callStartTime: z.string(),
        provider: z.string(),
        callEndTime: z.string().nullable().optional(),
        endedReason: z.string().nullable().optional(),
        summary: z.string().nullable().optional(),
        successEvaluation: z.string().nullable().optional(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough();
export type VoiceCallV2ReturnData = z.infer<typeof VoiceCallV2ReturnDataSchema>;
